// Get the base API URL from standard react app environment variables, 
// defaulting to localhost if missing during development
const BASE_URL = process.env.REACT_APP_API_URL || "https://localhost:44371/api";

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

interface RequestOptions {
    method?: HttpMethod;
    headers?: Record<string, string>;
    body?: any;
}

/**
 * Standardized API fetch wrapper that automatically attaches the Base URL 
 * and handles generic response parsing.
 * @param endpoint The API endpoint (e.g. `/auth/login`)
 * @param options HTTP mapping options
 * @returns Parsed JSON data and the raw Response object
 */
export const apiRequest = async (endpoint: string, options: RequestOptions = {}) => {
    const url = `${BASE_URL}${endpoint}`;
    
    // Auto-serialize JSON body and hook up base headers
    const headers = {
        "Content-Type": "application/json",
        ...options.headers,
    };

    const config: RequestInit = {
        method: options.method || "GET",
        headers,
    };

    if (options.body) {
        config.body = JSON.stringify(options.body);
    }

    try {
        const response = await fetch(url, config);
        const data = await response.json().catch(() => null); // Safely parse JSON if exists
        
        return { response, data };
    } catch (error) {
        console.error("API Request Error:", error);
        throw error;
    }
};

export const api = {
    get: (endpoint: string, headers?: Record<string, string>) => apiRequest(endpoint, { method: "GET", headers }),
    post: (endpoint: string, body: any, headers?: Record<string, string>) => apiRequest(endpoint, { method: "POST", body, headers }),
    put: (endpoint: string, body: any, headers?: Record<string, string>) => apiRequest(endpoint, { method: "PUT", body, headers }),
    delete: (endpoint: string, headers?: Record<string, string>) => apiRequest(endpoint, { method: "DELETE", headers })
};

export default api;
